<script setup lang="ts">
import { computed, watch } from 'vue'
import { ElAlert, ElEmpty, ElSkeleton, ElTable, ElTableColumn } from 'element-plus'
import { KsButton, KsModal, useFormatting, useIsMobile, useKsI18n } from '@siclop/kstone-vue'
import type { LeaveBalance } from '../models'
import { useLeaveAccruals } from '../composables/useLeaveAccruals'
import {
  formatLeaveDays, leaveAccrualReasonLabel, leaveBalanceDeadline, leaveBalancePeriodLabel
} from '../composables/leaveBalanceDisplay'

/** Détail d'un solde : chiffres, correction RH éventuelle et acquisition mois par mois (motifs traduits). */
const props = defineProps<{
  balance: LeaveBalance | null
  employeeName?: string
}>()

const visible = defineModel<boolean>({ required: true })

const { t } = useKsI18n()
const { isMobile } = useIsMobile()
const { formatDate } = useFormatting()
const { accruals, total, loading, error, load } = useLeaveAccruals()

watch(visible, open => {
  if (open && props.balance?.id) void load(props.balance.id)
}, { immediate: true })

const title = computed(() => {
  const b = props.balance
  if (!b) return t('ksproject.hr.leaveBalance.detail.title', 'Détail du solde')
  return t('ksproject.hr.leaveBalance.detail.titleWithType', '{type} {period}', {
    type: b.leaveTypeName || '-',
    period: leaveBalancePeriodLabel(b)
  })
})

const monthFormat = new Intl.DateTimeFormat('fr-FR', { month: 'long', year: 'numeric' })

/** « juin 2026 » */
function monthLabel(value: string): string {
  const [y, m] = (value ?? '').slice(0, 7).split('-').map(Number)
  if (!y || !m) return '-'
  return monthFormat.format(new Date(y, m - 1, 1))
}

const reasonLabel = (reason: unknown) => leaveAccrualReasonLabel(t, reason)
</script>

<template>
  <KsModal v-model="visible" :title="title" size="lg" append-to-body>
    <template v-if="balance">
      <p v-if="employeeName" class="detail-employee">{{ employeeName }}</p>

      <dl class="detail-summary">
        <div>
          <dt>{{ t('ksproject.hr.leaveBalance.period', 'Période') }}</dt>
          <dd>{{ formatDate(balance.periodStart) }} → {{ formatDate(balance.periodEnd) }}</dd>
        </div>
        <div>
          <dt>{{ t('ksproject.hr.leaveBalance.acquired', 'Acquis') }}</dt>
          <dd>{{ formatLeaveDays(balance.acquired) }}</dd>
        </div>
        <div>
          <dt>{{ t('ksproject.hr.leaveBalance.adjustment', 'Correction') }}</dt>
          <dd>{{ formatLeaveDays(balance.manualAdjustment) }}</dd>
        </div>
        <div>
          <dt>{{ t('ksproject.hr.leaveBalance.taken', 'Pris') }}</dt>
          <dd>{{ formatLeaveDays(balance.taken) }}</dd>
        </div>
        <div>
          <dt>{{ t('ksproject.hr.leaveBalance.pending', 'En attente') }}</dt>
          <dd>{{ formatLeaveDays(balance.pending) }}</dd>
        </div>
        <div>
          <dt>{{ t('ksproject.hr.leaveBalance.expired', 'Expiré') }}</dt>
          <dd>{{ formatLeaveDays(balance.expired) }}</dd>
        </div>
        <div>
          <dt>{{ t('ksproject.hr.leaveBalance.available', 'Disponible') }}</dt>
          <dd :class="{ 'is-negative': balance.remaining < 0 }"><strong>{{ formatLeaveDays(balance.remaining) }}</strong></dd>
        </div>
        <div>
          <dt>{{ t('ksproject.hr.leaveBalance.expiresOn', 'Expire le') }}</dt>
          <dd>{{ formatDate(leaveBalanceDeadline(balance)) }}</dd>
        </div>
      </dl>

      <el-alert
        v-if="balance.adjustmentReason"
        type="info"
        :closable="false"
        show-icon
        class="detail-adjustment"
        :title="t('ksproject.hr.leaveBalance.detail.adjustmentReason', 'Correction RH : {reason}', { reason: balance.adjustmentReason })"
      />

      <h4 class="detail-section">{{ t('ksproject.hr.leaveBalance.detail.accruals', 'Acquisition mois par mois') }}</h4>

      <el-skeleton v-if="loading" :rows="4" animated />
      <el-alert v-else-if="error" type="error" :title="error" :closable="false" show-icon />
      <el-empty
        v-else-if="accruals.length === 0"
        :description="t('ksproject.hr.leaveBalance.detail.noAccruals', 'Aucune ligne d\'acquisition')"
        :image-size="60"
      />

      <!-- Mobile : une ligne par mois, empilée -->
      <ul v-else-if="isMobile" class="accrual-list">
        <li v-for="line in accruals" :key="line.id" class="accrual-item">
          <div class="accrual-item-head">
            <span class="accrual-month">{{ monthLabel(line.month) }}</span>
            <strong>+{{ formatLeaveDays(line.accruedDays) }} {{ t('ksproject.hr.leaveRequest.daysShort', 'j') }}</strong>
          </div>
          <div class="accrual-item-meta">
            <span>{{ reasonLabel(line.reason) }}</span>
            <span>{{ t('ksproject.hr.leaveBalance.detail.presenceDays', 'Jours de présence') }} : {{ formatLeaveDays(line.presenceDays) }}</span>
          </div>
        </li>
        <li class="accrual-item accrual-total">
          <span>{{ t('ksproject.hr.leaveBalance.detail.total', 'Total acquis') }}</span>
          <strong>{{ formatLeaveDays(total) }} {{ t('ksproject.hr.leaveRequest.daysShort', 'j') }}</strong>
        </li>
      </ul>

      <el-table v-else :data="accruals" size="small" stripe show-summary :summary-method="() => ['', t('ksproject.hr.leaveBalance.detail.total', 'Total acquis'), formatLeaveDays(total), '']">
        <el-table-column :label="t('ksproject.hr.leaveBalance.detail.month', 'Mois')" min-width="130">
          <template #default="{ row }">{{ monthLabel(row.month) }}</template>
        </el-table-column>
        <el-table-column :label="t('ksproject.hr.leaveBalance.detail.presenceDays', 'Jours de présence')" width="150" align="right">
          <template #default="{ row }">{{ formatLeaveDays(row.presenceDays) }}</template>
        </el-table-column>
        <el-table-column :label="t('ksproject.hr.leaveBalance.detail.accruedDays', 'Jours acquis')" width="120" align="right">
          <template #default="{ row }">{{ formatLeaveDays(row.accruedDays) }}</template>
        </el-table-column>
        <el-table-column :label="t('ksproject.hr.leaveBalance.detail.reason', 'Motif')" min-width="180">
          <template #default="{ row }">{{ reasonLabel(row.reason) }}</template>
        </el-table-column>
      </el-table>
    </template>

    <template #footer>
      <KsButton :label="t('common.close', 'Fermer')" @click="visible = false" />
    </template>
  </KsModal>
</template>

<style scoped>
.detail-employee {
  margin: 0 0 12px;
  font-weight: 600;
}

.detail-summary {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 8px 16px;
  margin: 0 0 16px;
  padding: 12px;
  border-radius: 4px;
  background: var(--el-fill-color-light);
}

.detail-summary dt {
  font-size: 12px;
  color: var(--el-text-color-secondary);
}

.detail-summary dd {
  margin: 0;
}

.is-negative {
  color: var(--el-color-danger);
}

.detail-adjustment {
  margin-bottom: 16px;
}

.detail-section {
  margin: 0 0 8px;
  font-size: 14px;
}

.accrual-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.accrual-item {
  padding: 8px 0;
  border-bottom: 1px solid var(--el-border-color-lighter);
}

.accrual-item-head,
.accrual-total {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 8px;
}

.accrual-month {
  text-transform: capitalize;
}

.accrual-item-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 12px;
  font-size: 12px;
  color: var(--el-text-color-secondary);
}

.accrual-total {
  border-bottom: none;
  font-weight: 600;
}
</style>
